import { getCSRFToken } from "~/utils/csrf";

const OAUTH_HANDOFF_PATH = "/login/oauth-handoff";

/**
 * Extracts the OAuth login challenge from a redirect path, if present.
 * Returns null if the path is not an OAuth handoff or has no challenge.
 */
export function extractOAuthChallenge(path: string): string | null {
  try {
    const url = new URL(path, window.location.origin);
    // Reject absolute and protocol-relative URLs pointing off-origin --
    // otherwise "https://evil.example/login/oauth-handoff?challenge=..."
    // would match on pathname alone and let an attacker smuggle in their
    // own Hydra challenge.
    if (url.origin !== window.location.origin) {
      return null;
    }
    if (url.pathname !== OAUTH_HANDOFF_PATH) {
      return null;
    }
    // Treat an empty challenge (e.g. "?challenge=") as absent — callers test
    // truthiness, and "" is not a usable challenge, so normalize to null.
    return url.searchParams.get("challenge") || null;
  } catch {
    return null;
  }
}

/**
 * Performs the OAuth login handoff: binds the authenticated user to the login
 * challenge via a CSRF-protected POST to the monolith, then redirects to the
 * Hydra Provider callback. The handoff endpoint lives at the domain root (not
 * under the /canopy base path) and is served by the main monolith.
 *
 * Throws on network or server errors so the caller can surface the failure.
 */
export async function performOAuthHandoff(challenge: string): Promise<void> {
  const csrfToken = await getCSRFToken();
  const resp = await fetch(OAUTH_HANDOFF_PATH, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      "X-CSRF-TOKEN": csrfToken ?? "",
    },
    body: `challenge=${encodeURIComponent(challenge)}`,
  });

  if (!resp.ok) {
    throw new Error(`OAuth handoff failed: ${resp.status}`);
  }

  const { redirect_url: redirectUrl }: { redirect_url?: string } = await resp.json();
  if (!redirectUrl) {
    throw new Error("OAuth handoff response missing redirect_url");
  }
  window.location.assign(redirectUrl);
}
